import { getAppKeyRaw } from '@kurtosys/app-loader/dist/utils.js';
import { IApplication } from '@kurtosys/types/api/applicationManager/index.js';
import {
	CacheOptions,
	IApp,
	IAppRegistry,
	IAppsRender,
	IAppsRenderGroup,
	IAppsRenderVariables,
	IAppsRequest,
	IAppsRequestOptions,
	IAppsResponse,
	IAppUrl,
} from '@kurtosys/types/appsInitialize/index.js';
import { IAppsManagerInitializeOptions, IAppVersions, IManifest } from '@kurtosys/types/appsManager/index.js';
import { TExternalScript, TExternalScriptOptions } from '@kurtosys/types/utils/index.js';

import { isArrayLike } from '../common/commonUtils/isArrayLike.js';
import { commonUtils, helpers } from '../common/index.js';

import { AppHydration } from './AppHydration.js';
import { appendCacheQueryString, getElementsForAppInstance } from './utils.js';

export const EVENTS = {
	// eslint-disable-next-line @typescript-eslint/naming-convention
	KSYS_APP_LOADED: 'ksys-app-loaded',
	// eslint-disable-next-line @typescript-eslint/naming-convention
	KSYS_APPS_LOADED: 'ksys-apps-loaded',
	// eslint-disable-next-line @typescript-eslint/naming-convention
	KSYS_APP_EMBED_ADDED: 'ksys-app-embed-added',
};

export class AppsManager {
	constructor(
		public manifest: IManifest,
		public appHydration: AppHydration,
		public externalScriptsBasePath?: string,
	) {}
	async initialize(options: IAppsManagerInitializeOptions): Promise<{ elements: HTMLElement[] }> {
		const {
			initializeAppRequests,
			getApplications,
			externalScriptOptions,
			assetRegisterCacheOptions,
			appsRenderOptions,
			group,
		} = options;
		let { elements } = options;
		const templateIds = group && group.apps ? group.apps.map((app) => app.templateId) : undefined;
		if (!isArrayLike(elements) || elements.length === 0) {
			elements = this.getAppEmbedNodes(
				templateIds,
				(element: HTMLElement) => !element.dataset.managerInitialized,
			);
		}
		if (elements && elements.length > 0) {
			elements.forEach((element) => {
				if (element.dataset.ksysAppTemplateId !== 'ksys-app-manager') {
					element.dataset.managerInitialized = 'inprogress';
				}
			});
			const appsRequest = this.getAppsRequest(options, false, elements);
			if (appsRequest && appsRequest.apps && appsRequest.apps.length > 0) {
				const appsRequests = this.splitAppsRequest(appsRequest);
				const appsResponses = (initializeAppRequests && (await initializeAppRequests(appsRequests))) || [];
				const appsResponse = this.appHydration.mergeAppsResponses(
					appsResponses.filter((appResponse) => !(appResponse instanceof Error)),
				);
				this.setInputsOnEmbedContainers(options, elements);
				if (appsResponse) {
					this.mergeMasterStylesAndConfigurations(appsResponse);
					this.appHydration.setHydrationPoint(appsResponse);
					this.loadAppsResponseScripts(
						appsResponse,
						externalScriptOptions,
						assetRegisterCacheOptions,
						appsRenderOptions,
					);
				} else {
					await this.loadAppScriptsForAppsRequest(appsRequest, getApplications, externalScriptOptions);
				}
				elements.forEach((element) => {
					if (element.dataset.ksysAppTemplateId !== 'ksys-app-manager') {
						element.dataset.managerInitialized = 'true';
					}
				});
			}
		}
		return { elements };
	}
	isManagerAppNode(appParamsHelper: helpers.AppParamsHelper<any, any, any>): boolean {
		const templateId = this.manifest.ksysAppTemplateId;
		const templateIdWithVersion = `${this.manifest.ksysAppTemplateId}-v${this.manifest.version}`;
		const { ksysAppTemplateId } = appParamsHelper.rawAppParams;
		return [templateId, templateIdWithVersion].includes(ksysAppTemplateId);
	}

	orchestrateAppFromAppParams(
		appParams: helpers.AppParamsHelper<any, any, any>,
		includeInputs = false,
		appVersions?: IAppVersions,
	): IApp | null {
		const { rawAppParams } = appParams;
		const { ksysAppTemplateId, applicationCode, configurationKey, styleKey, ksysAppTemplateVersion } = rawAppParams;
		// Due to some DXM hacks, it's possible that the ksysAppTemplateId is not set on the appParams
		if (ksysAppTemplateId) {
			const app: IApp = {
				templateId: ksysAppTemplateId,
			};
			if (applicationCode) {
				app.applicationCode = applicationCode;
			}
			if (configurationKey) {
				app.configurationKey = configurationKey;
			}
			if (styleKey) {
				app.styleKey = styleKey;
			}
			if (ksysAppTemplateVersion) {
				app.version = ksysAppTemplateVersion;
			} else if (appVersions && appVersions[ksysAppTemplateId]) {
				app.version = appVersions[ksysAppTemplateId];
				if (!app.version.startsWith('v')) {
					app.version = `v${app.version}`;
				}
			}

			if (includeInputs) {
				app.inputs = appParams.values && appParams.values.inputs;
			}
			return app;
		}
		return null;
	}
	getAppEmbedNodes(templateIds?: string[], additionalFilterFn?: (element: HTMLElement) => boolean): HTMLElement[] {
		const ksysAppTemplateIdDataKey = 'data-ksys-app-template-id';
		const elements = document.querySelectorAll(`[${ksysAppTemplateIdDataKey}]`);
		let response = Array.from(elements) as HTMLElement[];
		if (templateIds && templateIds.length > 0) {
			response = response.filter((element) => {
				const { ksysAppTemplateId } = element.dataset;
				return ksysAppTemplateId === 'ksys-app-manager' || templateIds.includes(ksysAppTemplateId);
			});
		}
		return response.filter((element) => !additionalFilterFn || additionalFilterFn(element));
	}
	/**
	 * Returns an object that represents a request for a list of apps.
	 *
	 * @param {IAppsManagerInitializeOptions} options - The options for initializing the AppsManager.
	 * @param {boolean} [includeInputs=false] - Whether to include input values in the request.
	 * @param {HTMLElement[]} [elements] - An array of HTML elements to use as the basis for the request.
	 * @returns {IAppsRequest} An object that represents a request for a list of apps.
	 */
	getAppsRequest(
		options: IAppsManagerInitializeOptions,
		includeInputs = false,
		elements?: HTMLElement[],
	): IAppsRequest {
		const { url, appsRequestOptions, group, appVersions } = options;
		const appsRequest: IAppsRequest = {
			options: appsRequestOptions,
			apps: [],
		};
		if (!elements) {
			const templateIds = group && group.apps ? group.apps.map((app) => app.templateId) : undefined;
			elements = this.getAppEmbedNodes(
				templateIds,
				(element: HTMLElement) => !element.dataset.managerInitialized,
			);
		}

		if (elements && elements.length > 0) {
			const appParamHelpers = elements.map((element) => new helpers.AppParamsHelper(element, url));

			const managerAppParams = appParamHelpers.find((appParamsHelper) => this.isManagerAppNode(appParamsHelper));

			if (managerAppParams) {
				appsRequest.manager = this.orchestrateAppFromAppParams(managerAppParams, includeInputs);
				if (
					managerAppParams.values &&
					managerAppParams.values.inputs &&
					(managerAppParams.values.inputs as any).culture
				) {
					if (!appsRequest.options) {
						appsRequest.options = {};
					}
					appsRequest.options.culture = (managerAppParams.values.inputs as any).culture;
				}
			}
			const otherAppParams = appParamHelpers.filter((appParamsHelper) => !this.isManagerAppNode(appParamsHelper));

			for (const appParams of otherAppParams) {
				const app: IApp = this.orchestrateAppFromAppParams(appParams, includeInputs, appVersions);
				if (app) {
					if (!appsRequest.apps) {
						appsRequest.apps = [];
					}
					if (appsRequest.apps) {
						appsRequest.apps.push(app);
					}
				}
			}
		}
		return appsRequest;
	}

	setInputsOnEmbedContainers(options: IAppsManagerInitializeOptions, elements?: HTMLElement[]): void {
		const appsRequest = this.getAppsRequest(options, true, elements);
		const { manager, apps = [] } = appsRequest;
		if (manager) {
			for (const app of apps) {
				if (app) {
					app.inputs = Object.assign({}, manager.inputs || {}, app.inputs || {});
				}
			}
			for (const app of appsRequest.apps || []) {
				if (app && app.inputs) {
					const inputs = Object.keys(app.inputs).reduce((acc, inputKey) => {
						const key = `input${inputKey.substr(0, 1).toUpperCase()}${inputKey.substr(1)}`;
						const value = app.inputs[inputKey];
						acc[key] = value;
						return acc;
					}, {} as any);
					const elements = getElementsForAppInstance(app);
					if (elements && elements.length > 0) {
						for (const element of elements) {
							if (!element.dataset.ksysAppTemplateVersion && app.version) {
								element.dataset.ksysAppTemplateVersion = app.version;
							}
							for (const key of Object.keys(inputs)) {
								element.dataset[key] = inputs[key];
							}
						}
					}
				}
			}
		}
	}

	mergeMasterStylesAndConfigurations(appsResponse: IAppsResponse): void {
		const { apps = [], manager } = appsResponse;
		if (manager) {
			for (const appResponse of apps) {
				if (appResponse) {
					appResponse.applicationConfiguration = commonUtils.deepMergeObjects(
						manager.applicationConfiguration || {},
						appResponse.applicationConfiguration || {},
					);
					appResponse.applicationStyles = commonUtils.deepMergeObjects(
						manager.applicationStyles || {},
						appResponse.applicationStyles || {},
					);
				}
			}
		}
	}

	splitAppsRequest(originalAppsRequest: IAppsRequest, maxLength = 1900): IAppsRequest[] {
		const appsRequest = commonUtils.deepMergeObjects({}, originalAppsRequest);
		const response: IAppsRequest[] = [];
		const { options } = appsRequest;
		if (maxLength > 0) {
			let counter = 0;
			const maxCounter = 1000;
			const { apps = [], manager } = appsRequest;
			let currentAppsRequest: IAppsRequest = {
				manager,
				options,
				apps: [],
			};

			let workingLength = this.getUrlJsonLength(currentAppsRequest);

			for (let appIndex = 0; appIndex < apps.length; appIndex++) {
				if (workingLength) {
					const app = apps[appIndex];
					const appLength = this.getUrlJsonLength(app);
					if (appLength) {
						if (appLength + workingLength < maxLength) {
							if (!currentAppsRequest.apps) {
								currentAppsRequest.apps = [];
							}
							currentAppsRequest.apps.push(app);
							apps.shift();
							appIndex--;
							counter = 0;
						} else {
							response.push(currentAppsRequest);
							// eslint-disable-next-line @typescript-eslint/no-unused-vars
							const { culture, ...otherOptions } = (options || {}) as IAppsRequestOptions;
							currentAppsRequest = {
								options: otherOptions,
								apps: [],
							};
							appIndex--;
							counter++;
							if (counter > maxCounter) {
								break;
							}
						}
					} else {
						apps.shift();
						appIndex--;
					}
				}
				workingLength = this.getUrlJsonLength(currentAppsRequest);
			}
			if (currentAppsRequest) {
				response.push(currentAppsRequest);
			}
		} else {
			response.push(appsRequest);
		}
		return response;
	}
	getUrlJsonLength(obj: object): number | undefined {
		try {
			const str = JSON.stringify(obj);
			if (str && str.length > 0) {
				return encodeURIComponent(str).length;
			}
		} catch (ex) {
			console.warn('getUrlJsonLength', ex);
		}
		return undefined;
	}
	loadAppsResponseScripts(
		appsResponse: IAppsResponse,
		externalScriptOptions?: TExternalScriptOptions,
		assetRegisterCacheOptions?: CacheOptions,
		appsRenderOptions?: IAppsRender,
	): void {
		if (appsResponse.javascript) {
			if (typeof appsResponse.javascript === 'string') {
				const scriptElement = document.createElement('script');
				scriptElement.type = 'text/javascript';
				scriptElement.innerHTML = appsResponse.javascript;
				const currentScript = this.getCurrentScript();
				if (currentScript && currentScript.parentNode) {
					currentScript.parentNode.insertBefore(scriptElement, currentScript);
				}
				appsResponse.javascript = 'true';
			}
		} else {
			this.loadAppScripts(appsResponse, externalScriptOptions, assetRegisterCacheOptions, appsRenderOptions);
		}
	}

	loadAppScripts(
		appsResponse: IAppsResponse,
		externalScriptOptions?: TExternalScriptOptions,
		cacheOptions?: CacheOptions,
		appsRenderOptions?: IAppsRender,
	): void {
		const { apps } = appsResponse;
		const scriptUrls: IAppUrl[] = (apps || []).map((appResponse) => {
			const { app } = appResponse;
			let fileName = `main-${appResponse.app.templateId}.js`;
			fileName = fileName.replace(/-v\d{1,5}\.\d{1,5}\.\d{1,5}/, '');
			if (app.assetRegister && app.assetRegister.length > 0) {
				const assetRegister = app.assetRegister.find((asset) => asset.fileName === fileName);
				if (assetRegister) {
					fileName = appendCacheQueryString(fileName, assetRegister, cacheOptions);
				}
			}
			return {
				app,
				url: `/applicationManager/apps/${appResponse.app.applicationCode}/${fileName}`,
			};
		});

		const baseUrl = this.externalScriptsBasePath || this.manifest.getBaseUrl('');
		renderApps(this.manifest, scriptUrls, baseUrl, externalScriptOptions, appsRenderOptions);
	}

	async loadAppScriptsForAppsRequest(
		appsRequest: IAppsRequest,
		getApplications: () => Promise<IApplication[]>,
		externalScriptOptions?: TExternalScriptOptions,
	): Promise<void> {
		const applications = (getApplications && (await getApplications())) || [];
		const applicationsByApplicationTemplateCode = commonUtils.pivotCollection(
			applications,
			(application) => application.applicationTemplateCode || '_noApplicationTemplateCode',
		);
		const { apps = [] } = appsRequest;
		const scriptUrls = apps.map((app: IApp) => {
			const { templateId, version = 'v1.0.0' } = app;
			const applicationTemplateCode = templateId;
			const applicationTemplateCodeWithVersion = `${applicationTemplateCode}-${version}`;
			const application =
				applicationsByApplicationTemplateCode[applicationTemplateCodeWithVersion] ||
				applicationsByApplicationTemplateCode[applicationTemplateCode];
			if (application) {
				const { applicationCode } = application;
				let fileName = `main-${app.templateId}.js`;
				fileName = fileName.replace(/-v\d{1,5}\.\d{1,5}\.\d{1,5}/, '');
				return `/applicationManager/apps/${applicationCode}/${fileName}`;
			}
			console.warn(
				`No application found for: ${applicationTemplateCode} or ${applicationTemplateCodeWithVersion}`,
			);
			return;
		});

		const baseUrl = this.externalScriptsBasePath || this.manifest.getBaseUrl('');
		for (const scriptUrl of scriptUrls) {
			loadScriptWithOptions(this.manifest, scriptUrl, baseUrl, externalScriptOptions);
		}
	}
	getCurrentScript(): HTMLOrSVGScriptElement {
		return (
			document.currentScript ||
			(function (): HTMLOrSVGScriptElement {
				const scripts = document.getElementsByTagName('script');
				return scripts[scripts.length - 1];
			})()
		);
	}
}

export function getAppRegistry(): IAppRegistry {
	const key = '__ksys-app-registry__';
	if (!(window as any)[key]) {
		(window as any)[key] = {};
	}
	return (window as any)[key];
}

export function getAppsRenderVariables(): IAppsRenderVariables {
	const key = '__ksys-app-render-variables__';
	if (!(window as any)[key]) {
		(window as any)[key] = {};
	}
	return (window as any)[key];
}

export function renderAppsGroup(
	manifest: IManifest,
	appsGroup: IAppsRenderGroup,
	baseUrl: string,
	externalScriptOptions?: TExternalScriptOptions,
): void {
	if (appsGroup) {
		const appsRenderVariables = getAppsRenderVariables();
		const { appUrlsByAppKey } = appsRenderVariables;
		if (appUrlsByAppKey) {
			const { apps } = appsGroup;
			for (const app of apps) {
				const appKey = getAppKeyRaw(app.templateId, app.configurationKey, app.styleKey, app.version);
				const appsUrl = appUrlsByAppKey[appKey];
				if (appsUrl) {
					loadScriptWithOptions(manifest, appsUrl.url, baseUrl, externalScriptOptions, appKey);
				}
			}
		}
	}
}

export function renderApps(
	manifest: IManifest,
	appUrls: IAppUrl[],
	baseUrl: string,
	externalScriptOptions?: TExternalScriptOptions,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	appsRenderOptions?: IAppsRender,
): void {
	if (appUrls) {
		for (const appUrl of appUrls) {
			loadScriptWithOptions(manifest, appUrl.url, baseUrl, externalScriptOptions);
		}
	}
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function removeAppsLoadedEventListener(): void {
	document.removeEventListener(EVENTS.KSYS_APP_LOADED, window['__ksys-app-handle-app-loaded__']);
}

export function loadScriptWithOptions(
	manifest: IManifest,
	src: string,
	baseUrl: string,
	externalScriptOptions?: TExternalScriptOptions,
	appKey?: string,
): void {
	if (src) {
		try {
			const externalScript: TExternalScript = {
				src,
				options: externalScriptOptions || {},
			};
			commonUtils.loadScript(externalScript, baseUrl);
			if (appKey) {
				const appsRenderVariables = getAppsRenderVariables();
				const { appUrlsByAppKey } = appsRenderVariables;
				delete appUrlsByAppKey[appKey];
			}
		} catch (ex) {
			console.warn(ex);
		}
	}
}
